import { useEffect, useState } from 'react';
import {
  getLandingDomains,
  createLandingDomain,
  updateLandingDomain,
  type LandingDomain,
} from '../api/marketing';
import { ApiError } from '../api/client';
import { Button } from '../components/arc/button/button';
import { CompactSelect } from '../components/arc/compact-select/compact-select';
import { ArcPanel } from '../components/arc/primitives/arc-primitives';
import './domain-settings-arc.css';

const COUNTRIES = ['CZ', 'SK', 'RO'];
const FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };

interface FormState {
  country: string;
  hostname: string;
  sftp_user: string;
  sftp_host: string;
  webroot: string;
}
const EMPTY: FormState = { country: 'CZ', hostname: '', sftp_user: '', sftp_host: '', webroot: '' };

export function LandingSettings() {
  const [items, setItems] = useState<LandingDomain[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);

  function reload() {
    getLandingDomains()
      .then((d) => { setItems(d); setError(null); })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo'));
  }
  useEffect(reload, []);

  function startAdd() { setForm(EMPTY); setEditId(null); setAdding(true); }
  function startEdit(d: LandingDomain) {
    setForm({ country: d.country, hostname: d.hostname, sftp_user: d.sftpUser ?? '', sftp_host: d.sftpHost ?? '', webroot: d.webroot ?? '' });
    setAdding(false);
    setEditId(d.id);
  }
  function cancel() { setAdding(false); setEditId(null); setForm(EMPTY); }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      if (adding) {
        await createLandingDomain({
          country: form.country,
          hostname: form.hostname.trim(),
          sftp_user: form.sftp_user.trim() || undefined,
          sftp_host: form.sftp_host.trim() || undefined,
          webroot: form.webroot.trim() || undefined,
        });
      } else if (editId != null) {
        await updateLandingDomain(editId, {
          sftp_user: form.sftp_user.trim(),
          sftp_host: form.sftp_host.trim(),
          webroot: form.webroot.trim(),
        });
      }
      cancel();
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(d: LandingDomain) {
    try {
      await updateLandingDomain(d.id, { active: !d.active });
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Změna stavu selhala');
    }
  }

  const editing = adding || editId != null;

  return (
    <ArcPanel className="p1-domain-settings p1-arc-scope">
      <div className="p1-domain-settings-head">
        <h2>Landing domény</h2>
        {!editing && (
          <Button type="button" size="sm" onClick={startAdd}>Přidat doménu</Button>
        )}
      </div>
      <div className="p1-domain-settings-body">
        {error && <div className="alert alert-danger py-2">{error}</div>}

        {editing && (
          <div className="p1-domain-settings-form">
            <div className="row g-2">
              <div className="col-md-3">
                <label className="form-label small mb-1">Země</label>
                <CompactSelect value={form.country} disabled={!adding} ariaLabel="Země" options={COUNTRIES.map((c) => ({ value: c, label: `${FLAG[c]} ${c}` }))}
                  onChange={(country) => setForm({ ...form, country })} />
              </div>
              <div className="col-md-9">
                <label className="form-label small mb-1">Doména (hostname)</label>
                <input className="form-control form-control-sm" placeholder="akce.rondo.cz" value={form.hostname}
                  disabled={!adding}
                  onChange={(e) => setForm({ ...form, hostname: e.target.value })} />
                {!adding && <div className="form-text">Země + hostname jsou read-only (změna = nový záznam).</div>}
              </div>
              <div className="col-md-6">
                <label className="form-label small mb-1">SFTP host</label>
                <input className="form-control form-control-sm" placeholder="62.4.5.163" value={form.sftp_host}
                  onChange={(e) => setForm({ ...form, sftp_host: e.target.value })} />
              </div>
              <div className="col-md-6">
                <label className="form-label small mb-1">SFTP uživatel</label>
                <input className="form-control form-control-sm" placeholder="www-data" value={form.sftp_user}
                  onChange={(e) => setForm({ ...form, sftp_user: e.target.value })} />
              </div>
              <div className="col-12">
                <label className="form-label small mb-1">Webroot</label>
                <input className="form-control form-control-sm" placeholder="/home/www/akce.rondo.cz/www" value={form.webroot}
                  onChange={(e) => setForm({ ...form, webroot: e.target.value })} />
                <div className="form-text">SSH klíč/heslo se v DB neukládá (jen v .env jako *_REF).</div>
              </div>
            </div>
            <div className="p1-domain-settings-actions">
              <Button type="button" size="sm" disabled={saving || (adding && !form.hostname.trim())} onClick={() => void save()}>
                {saving ? 'Ukládám…' : 'Uložit'}
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={cancel} disabled={saving}>Zrušit</Button>
            </div>
          </div>
        )}

        {items == null ? (
          <div className="text-muted py-4"><span className="spinner-border spinner-border-sm me-2" />Načítám…</div>
        ) : items.length === 0 ? (
          <div className="text-muted py-4 text-center">Zatím žádná landing doména.</div>
        ) : (
          <div className="p1-domain-settings-list">
            {items.map((d) => (
              <div key={d.id} className="p1-domain-settings-row">
                <div className="p1-domain-settings-row-copy">
                  <div className="p1-domain-settings-row-title">
                    <span>{FLAG[d.country] ?? ''}</span>
                    <strong>{d.hostname}</strong>
                    {d.active
                      ? <span className="p1-domain-settings-badge" data-state="active">Aktivní</span>
                      : <span className="p1-domain-settings-badge">Neaktivní</span>}
                  </div>
                  <div className="p1-domain-settings-row-meta">
                    <span>{[d.sftpHost && `SFTP: ${d.sftpHost}`, d.webroot].filter(Boolean).join(' · ') || 'Bez dalšího popisu'}</span>
                  </div>
                </div>
                <div className="p1-domain-settings-row-actions">
                  <Button type="button" variant="ghost" size="sm" onClick={() => void toggleActive(d)}>{d.active ? 'Deaktivovat' : 'Aktivovat'}</Button>
                  <Button type="button" variant="secondary" size="sm" onClick={() => startEdit(d)}>Upravit</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ArcPanel>
  );
}
